import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Modal, message } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import ColumnSettings from '../../../components/table/ColumnSettings';
import PPResizableTable from '../../../components/table/PPResizableTable';
import { useColumnSettings } from '../../../components/table/useColumnSettings';
import useStore from '../../../store/useStore';
import { getFormattedResponse, getObjectByKey } from '../../../utils';
import { useTableFilter } from '../../../utils/Filter';
import { exportToXLSX } from '../../../utils/xlsxHelper';
import {
  deleteClient,
  getClient,
  listAllClients,
  listClients,
  updateClient,
} from '../clientService';
import EditClient from '../Edit';
import { getColumns } from './columns';
import useCustomerPriceUpload from './useCustomerPriceUpload';

import '../style.less';

const ClientsActive = () => {
  const isB2B = useStore((state) => state?.user?.isB2B);
  const [form] = Form.useForm();
  const confirm = Modal.confirm;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [editRowId, setEditRowId] = useState();
  const [address, setAddress] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  // Засах модал нээхийн өмнө дэлгэрэнгүйг татаж буй мөрийн `_id`
  const [editLoadingId, setEditLoadingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // `tab` нь Clients.js-ийн табын URL параметр тул шүүлтүүр болгож API руу явуулахгүй
  const fetchData = async ({ page = 1, pageSize = 10, tab, ...filters } = {}) => {
    setLoading(true);
    try {
      const response = await listClients({ page, pageSize, ...filters });
      const {
        list,
        totalDocs,
        page: currentPage,
        pageSize: currentSize,
      } = getFormattedResponse(response);

      setData(list);
      setPagination({
        current: currentPage ?? page,
        pageSize: currentSize ?? pageSize,
        total: totalDocs ?? 0,
      });
    } catch (error) {
      // Алдааны мэдэгдлийг axios interceptor (App.js) аль хэдийн гаргасан
      console.error('Client list fetch error: ', error);
    } finally {
      setLoading(false);
    }
  };

  const { getColumnSearchProps, handleTableChange, withHighlight, filters } = useTableFilter({
    fetchData,
  });

  const refreshList = () => fetchData({ page: pagination.current, pageSize: pagination.pageSize });

  const { openFileDialog, fileInput, uploadingId } = useCustomerPriceUpload({
    onUploaded: refreshList,
  });

  const closeEditModal = () => {
    setIsEdit(false);
    setAddress([]);
  };

  const handleEdit = async (_id) => {
    setEditRowId(_id);
    setEditLoadingId(_id);

    try {
      const response = await getClient(_id);
      if (response?.statusCode !== 200) return;

      const fields = response?.result?.data ?? [];
      const addressUnits = getObjectByKey(fields, 'address')?.value ?? [];

      setAddress(addressUnits);
      form.setFieldsValue({
        email: getObjectByKey(fields, 'email')?.value,
        name: getObjectByKey(fields, 'name')?.value,
        phoneNumber: getObjectByKey(fields, 'phoneNumber')?.value,
        description: getObjectByKey(fields, 'description')?.value,
        addressDetail: getObjectByKey(fields, 'addressDetail')?.value,
        // Хаяг нь аймаг/сум/баг гэсэн 3 хэсгээс бүрдэх ба дутуу байж болно
        aimagHot: addressUnits[0]?.name,
        sumDuureg: addressUnits[1]?.name,
        bagHoroo: addressUnits[2]?.name,
      });
      setIsEdit(true);
    } catch (error) {
      // Алдааны мэдэгдлийг axios interceptor (App.js) аль хэдийн гаргасан
      console.error('Client detail fetch error: ', error);
    } finally {
      setEditLoadingId(null);
    }
  };

  const submitModalEdit = () => {
    form.validateFields().then(async (values) => {
      setSaving(true);
      try {
        const response = await updateClient(editRowId, { ...values, address });
        if (response?.statusCode !== 200) return;

        closeEditModal();
        message.success('Амжилттай шинэчлэгдлээ');
        refreshList();
      } catch (error) {
        // Алдааны мэдэгдлийг axios interceptor (App.js) аль хэдийн гаргасан
        console.error('Client update error: ', error);
      } finally {
        setSaving(false);
      }
    });
  };

  const handleDelete = (_id) =>
    confirm({
      title: 'Харилцагчийг устгах уу?',
      okText: 'Устгах',
      cancelText: 'Цуцлах',
      okType: 'danger',
      async onOk() {
        try {
          const response = await deleteClient(_id);
          if (response?.statusCode !== 200) return;

          message.success('Харилцагч амжилттай устгагдлаа');
          refreshList();
        } catch (error) {
          // Алдааны мэдэгдлийг axios interceptor (App.js) аль хэдийн гаргасан
          console.error('Client delete error: ', error);
        }
      },
    });

  const handleExport = async () => {
    let list;
    setExporting(true);
    try {
      // Хүснэгтэд харагдаж буй шүүлтээр нь татна — эс тэгвээс хэрэглэгч шүүсэн
      // үр дүнгээ хүлээж байтал бүх харилцагч бүхий файл татагдана
      const response = await listAllClients(filters);
      if (response?.statusCode !== 200) return;

      list = getFormattedResponse(response).list;
    } catch (error) {
      // Алдааны мэдэгдлийг axios interceptor (App.js) аль хэдийн гаргасан
      console.error('Client export fetch error: ', error);
      return;
    } finally {
      setExporting(false);
    }

    if (!list?.length) {
      message.warning('Шүүлтэд тохирох харилцагч олдсонгүй. Татах мэдээлэл байхгүй.');
      return;
    }

    // Файл үүсгэх нь client талд явагддаг тул interceptor мэдэгдэхгүй — энд хэлнэ
    try {
      exportToXLSX({
        data: list,
        columns: ['name', 'phoneNumber', 'email', 'addressDetail'],
        columnNames: ['Нэр', 'Утасны дугаар', 'и-мэйл', 'Дэлгэрэнгүй хаяг'],
        fileName: 'Харилцагчид',
      });
    } catch (error) {
      console.error('Client export error: ', error);
      message.error('Excel татахад алдаа гарлаа.');
    }
  };

  const { columns, settingsProps, onColumnOrderChange } = useColumnSettings({
    columns: withHighlight(
      getColumns({
        getColumnSearchProps,
        pagination,
        isB2B,
        onEdit: handleEdit,
        onDelete: handleDelete,
        onImport: openFileDialog,
        editLoadingId,
        uploadingId,
      })
    ),
    storageKey: 'clients.list',
  });

  return (
    <>
      <div className="pp_table_wrapper">
        <div className="clients_toolbar">
          <ColumnSettings {...settingsProps} />

          <Link to="/clients/add">
            <Button icon={<PlusOutlined />} type="primary">
              Шинэ
            </Button>
          </Link>

          <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Export
          </Button>
        </div>

        <PPResizableTable
          rowKey="_id"
          columns={columns}
          onColumnOrderChange={onColumnOrderChange}
          dataSource={data}
          loading={loading}
          size="middle"
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Нийт ${total} илэрц`,
          }}
          scroll={{ x: 1500 }}
        />
      </div>

      {fileInput}

      {isEdit && (
        <Modal
          title="Харилцагч засах"
          visible={isEdit}
          okText="Хадгалах"
          cancelText="Болих"
          onOk={submitModalEdit}
          confirmLoading={saving}
          onCancel={closeEditModal}
          width={800}
        >
          <EditClient
            form={form}
            isArchived={false}
            addressUnits={address}
            setAddressUnits={setAddress}
          />
        </Modal>
      )}
    </>
  );
};

export default ClientsActive;
